import { z } from "zod";

import { PERMISSION_DOMAINS } from "../permissions";

/**
 * Bildirim — Üst Bar panelinin TÜKETTİĞİ projeksiyon (CLOS-103 `[064]`).
 *
 * ### Neden şema bu kartta
 *
 * Kartın kapsamı *"kategori ve çalışma alanı etiketi, sınırlı bildirim önizlemesi"*
 * diyor. Kabuk bir bildirimi çizebilmek için şeklini bilmek zorunda; şema olmadan kart
 * yazılamazdı. Projeksiyonu ÜRETEN kod bu kartta değil: olayları ve Bildirim Merkezini
 * CLOS-186, okundu durumu/tercih/teslimi CLOS-187 geliştiriyor.
 *
 * ### ⚠ Bildirim ≠ Bekleyen İşlem
 *
 * Kartın ilk kriteri bu ayrımı şart koşuyor. Bekleyen İşlem (`[063]`) kullanıcıya
 * ATANMIŞ bir iştir ve bir karar bekler; bildirim yalnız haberdar eder. Bu yüzden iki
 * ayrı şema, iki ayrı uç ve iki ayrı Üst Bar öğesi var — tek bir "kutu" altında
 * birleştirmek, okumayı yapmakla karıştırırdı.
 */

/**
 * Bildirim kategorisi.
 *
 * ⚠ Taksonomi UYDURULMADI: yetki kataloğunun domainleri (ADR-0021). Ürünün bütün
 * bildirimleri bu altı alandan birine ait — davet `organization`, imza `contract`.
 * Yeni bir kategori seti üretilseydi aynı kavram iki sözlükte iki adla yaşardı ve
 * "bu bildirimi görmeye kimin hakkı var" sorusu iki ayrı eksende cevaplanırdı.
 *
 * Olay → Bildirim/Bekleyen İşlem/Aktivite YÖNLENDİRMESİ bu dosyanın işi değil; o bir
 * ürün kararı ve CLOS-323'ün matrisine ait (olay kataloğunun kendi notu). Kategori
 * ekseni CLOS-186'da değişirse tek yer burasıdır.
 */
/**
 * BİLDİRİM KRİTİKLİK TAKSONOMİSİ (CLOS-1244 — kanon §12).
 *
 * ### ⚠ Bu bir ÖNCELİK ekseni, bir ZORUNLULUK ekseni DEĞİL
 *
 * `notificationConfig.ts` bu ayrımı zaten kurmuş ve gerekçesi aynen geçerli: "kullanıcı
 * tercihiyle kapatılamaz" bilgisi `mandatoryEvents` konfigünde yaşıyor, burada değil. İki
 * kavramı tek alana bindirmek, yarın "yüksek öncelikli ama isteğe bağlı" bir bildirim
 * gerektiğinde çözümsüz kalırdı. Kanon da ikisini ayrı bölümlerde tanımlıyor (§12 sınıf,
 * §21.3 tercih sınırı).
 *
 * ### ⚠ SIRA ANLAMLIDIR
 *
 * Dizi artan önem sırasında ve karşılaştırma bunun üzerinden yapılıyor. Alfabetik ya da
 * rastgele sıralasaydık "en az ATTENTION" gibi bir kural yazılamaz, her karşılaştırma
 * elle bir öncelik haritası uydururdu.
 */
export const NOTIFICATION_CRITICALITIES = [
  "info",
  "attention",
  "action_required",
  "critical",
] as const;
export type NotificationCriticality = (typeof NOTIFICATION_CRITICALITIES)[number];

/**
 * Kullanıcıya görünen adlar.
 *
 * ⚠ SEVİYE METİNLE ANLATILIYOR, yalnız renkle değil (rapor 4 §21 — erişilebilirlik).
 * Rengi göremeyen kullanıcı kritik bir uyarıyı sıradan bir haberden ayırt edebilmeli.
 * `INFO` bilinçli olarak BOŞ: her satıra "Bilgi" yazmak, hiçbir şey söylemeyen bir etiketi
 * her satıra basmak olurdu — ayırt edici olan, sıradan olanın işaretsiz kalmasıdır.
 */
export const NOTIFICATION_CRITICALITY_NAMES: Record<NotificationCriticality, string | null> = {
  info: null,
  attention: "Takip gerek",
  action_required: "Eylem bekleniyor",
  critical: "Kritik",
};

/**
 * Rozet tonu — `@closer/ui` `Badge` sözleşmesinin kendi kelimeleri.
 *
 * ⚠ Dört seviyeye DÖRT AYRI ton verilmedi ama üç ton verildi: `attention` uyarı,
 * `action_required` birincil (yapılacak bir şey var), `critical` tehlike. İkisine aynı
 * tonu verseydik renk ayrımı kaybolurdu — ama asıl ayrımı zaten METİN taşıyor, ton
 * yalnız onu destekliyor. `info` hiç rozet çizmediği için tonu kullanılmıyor; yine de
 * geçerli bir değer, çünkü eksik bir anahtar tipi kırar.
 */
export const NOTIFICATION_CRITICALITY_TONES: Record<
  NotificationCriticality,
  "neutral" | "warning" | "primary" | "danger"
> = {
  info: "neutral",
  attention: "warning",
  action_required: "primary",
  critical: "danger",
};

export const notificationSchema = z.object({
  id: z.uuid(),
  category: z.enum(PERMISSION_DOMAINS),
  /**
   * Bildirimin önem sınıfı (CLOS-1244 — kanon §12).
   *
   * ⚠ ALAN BUGÜNE KADAR SATIRDA VARDI AMA PROJEKSİYONDA YOKTU: `notifications.criticality`
   * yazılıyor, hiçbir yüzey okumuyordu (ölçüldü — `apps/web`'de 0 eşleşme). Yazılan ve hiç
   * okunmayan bir alan, ürün davranışı değil ölü ağırlıktır.
   */
  criticality: z.enum(NOTIFICATION_CRITICALITIES),
  /** Kullanıcıya gösterilen tek satırlık özet — projeksiyon üretir. */
  title: z.string(),
  /**
   * Bildirimi doğuran kanonik olayın adı (CLOS-232 dönüşü).
   *
   * ⚠ EKLENDİ, KATEGORİNİN YERİNE GEÇMEDİ: kategori altı yetki domaininden biri ve
   * "bu satırı görmeye kimin hakkı var" sorusuna hizmet ediyor; olay adı ise "ne oldu"
   * sorusuna. Kategoriyle ayırt edilemeyen iki bildirim (aynı sözleşmeden gelen tarih
   * hatırlatması ile inceleme hatırlatması) ancak bu alanla ayrılıyor.
   *
   * ⚠ Satırda ZATEN duruyordu (`notifications.event_name`); yalnız projeksiyona
   * çıkarıldı. Kişisel veri taşımıyor — katalogdaki sabit bir ad.
   */
  eventName: z.string(),
  /**
   * Kaynak kaydın görünen adı; kayıt bağı yoksa ya da çözülemediyse `null`.
   *
   * ### ⚠ OKUMA ANINDA ÇÖZÜLÜYOR — satıra KOPYALANMIYOR
   *
   * Kart (CLOS-186 `[168]`): *"kullanıcının erişemediği kayıt bilgisi bildirimde
   * sızmaz."* Adı üretim anında `title`'a yazsaydık satırın KENDİSİ veriyi taşırdı ve
   * erişim sonradan kalksa bile o kopya (e-posta teslimi dahil) geri alınamazdı.
   *
   * Bu alan bunun yerine listeleme sırasında, erişim süzgeci ÇALIŞTIKTAN SONRA
   * dolduruluyor: adı gören herkes zaten kaydı da görebiliyor. Aynı bilgi, kalıcı kopya
   * olmadan.
   */
  recordName: z.string().nullable(),
  /**
   * Kayda giden yol; kayıt bağı olmayan bildirimde `null` (CLOS-186 `[168]`).
   *
   * ⚠ Bu bir KAPI değil, bir ADRES: hedef açılırken erişim yeniden sorulur (CLOS-93).
   */
  href: z.string().nullable(),
  createdAt: z.string(),
  /**
   * Okunmamışsa `null`. ⚠ Kabuk bu alanı yalnız OKUYOR: okundu durumunu CLOS-187
   * yönetiyor ve kartın kriteri *"bildirimi okumak ilgili işlemi tamamlamaz"* — okuma
   * bir haber alma, işlem ise bir karardır.
   */
  readAt: z.string().nullable(),
});
export type AppNotification = z.infer<typeof notificationSchema>;

/** Okunmamış sayacı — rozetin tek kaynağı. */
export function unreadCount(notifications: readonly AppNotification[]): number {
  return notifications.filter((n) => n.readAt === null).length;
}

/**
 * BİLDİRİM LİSTESİ YANITI (CLOS-187 `[169]`).
 *
 * ### ⚠ OKUNMAMIŞ SAYISI SUNUCUDAN — istemci saymıyor
 *
 * CLOS-186'da rozet, çekilen listeden sayılıyordu (`unreadCount(bildirimler)`). Liste
 * sunucuda TAVANLI olduğu için bu, tavanı aşan okunmamış sayısında sessizce YANLIŞ bir
 * rozet üretirdi — kartın *"Üst Bar rozeti Bildirim Merkeziyle tutarlıdır"* kriterinin
 * tam ihlali. Sayı artık listeden bağımsız, kendi sorgusundan geliyor.
 *
 * ### ⚠ POLL ARALIĞI DA BURADA
 *
 * İstemci bir sayı gömmüyor; aralık `config/notifications.json`'dan gelip yanıtla
 * taşınıyor (ADR-0037). Ayrı bir "ayarlar" isteği açsaydık her açılışta ikinci bir tur
 * gerekirdi.
 */
export const notificationListSchema = z.object({
  items: z.array(notificationSchema),
  unread: z.number().int().min(0),
  pollIntervalMs: z.number().int().min(1),
});
export type NotificationList = z.infer<typeof notificationListSchema>;
